import {
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

/**
 * Shared behavior for the custom dropdowns used in the onboarding flow.
 *
 * Covers what the onboarding dropdowns previously did by hand (and the
 * PR-review a11y gaps: no outside-click, no keyboard nav, missing ARIA):
 *   - open / close / toggle state
 *   - mousedown + touchstart outside-click, with optional extra refs for
 *     portaled menus (role dropdown in UserOnboarding)
 *   - keyboard: ArrowUp, ArrowDown, Home, End, Enter, Escape
 *   - stable ids for aria-controls / aria-activedescendant
 *   - activeIndex clamping when the filtered itemCount shrinks
 *
 * The caller decides where onKeyDown is attached — on the trigger button for
 * plain dropdowns, or on the in-menu search input for search-enabled ones.
 */
export interface UseListboxComboboxOptions {
  itemCount: number;
  onSelect: (index: number) => void;
  /** Extra containers considered "inside" for outside-click. Use for portaled menus. */
  additionalRefs?: ReadonlyArray<RefObject<HTMLElement | null>>;
}

export interface UseListboxComboboxResult {
  isOpen: boolean;
  activeIndex: number;
  containerRef: RefObject<HTMLDivElement>;
  open: () => void;
  close: () => void;
  toggle: () => void;
  setActiveIndex: (index: number) => void;
  /** Invokes onSelect(index) and closes the menu. Use from both Enter and mouse click so both paths behave identically. */
  select: (index: number) => void;
  /**
   * Attach to whichever focusable element should own keyboard nav while the
   * menu is open (the trigger button for plain dropdowns; the in-menu search
   * input for search-enabled ones). Works whether the menu is open or closed:
   * when closed, ArrowDown/ArrowUp/Enter open it.
   */
  onKeyDown: (e: KeyboardEvent) => void;
  menuId: string;
  getOptionId: (index: number) => string;
  activeOptionId: string | undefined;
}

export function useListboxCombobox({
  itemCount,
  onSelect,
  additionalRefs,
}: UseListboxComboboxOptions): UseListboxComboboxResult {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const reactId = useId();
  const menuId = `listbox-${reactId}`;

  const getOptionId = useCallback(
    (index: number) => `${menuId}-opt-${index}`,
    [menuId],
  );

  const activeOptionId =
    activeIndex >= 0 && activeIndex < itemCount
      ? getOptionId(activeIndex)
      : undefined;

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) setActiveIndex(-1);
      return !prev;
    });
  }, []);

  // Clamp activeIndex when the filtered/visible itemCount shrinks, so the
  // highlight never points past the end of the list.
  useEffect(() => {
    if (itemCount === 0) {
      if (activeIndex !== -1) setActiveIndex(-1);
    } else if (activeIndex >= itemCount) {
      setActiveIndex(itemCount - 1);
    }
  }, [itemCount, activeIndex]);

  // Mousedown + touchstart outside the container (and any additional refs)
  // closes the menu. Matches the pre-existing pattern on the role dropdown.
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (additionalRefs) {
        for (const ref of additionalRefs) {
          if (ref.current?.contains(target)) return;
        }
      }
      close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [isOpen, close, additionalRefs]);

  const select = useCallback(
    (index: number) => {
      if (index < 0 || index >= itemCount) return;
      onSelect(index);
      setIsOpen(false);
      setActiveIndex(-1);
    },
    [itemCount, onSelect],
  );

  const moveActive = useCallback(
    (delta: number) => {
      if (itemCount === 0) {
        setActiveIndex(-1);
        return;
      }
      setActiveIndex((prev) => {
        const from = prev < 0 ? (delta > 0 ? -1 : itemCount) : prev;
        const next = from + delta;
        if (next < 0) return itemCount - 1;
        if (next >= itemCount) return 0;
        return next;
      });
    },
    [itemCount],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            setActiveIndex(itemCount > 0 ? 0 : -1);
          } else {
            moveActive(1);
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            setActiveIndex(itemCount > 0 ? itemCount - 1 : -1);
          } else {
            moveActive(-1);
          }
          break;
        case 'Home':
          if (isOpen && itemCount > 0) {
            e.preventDefault();
            setActiveIndex(0);
          }
          break;
        case 'End':
          if (isOpen && itemCount > 0) {
            e.preventDefault();
            setActiveIndex(itemCount - 1);
          }
          break;
        case 'Enter':
          if (isOpen && activeIndex >= 0 && activeIndex < itemCount) {
            e.preventDefault();
            select(activeIndex);
          }
          break;
        case 'Escape':
          if (isOpen) {
            e.preventDefault();
            close();
          }
          break;
        default:
          break;
      }
    },
    [isOpen, itemCount, activeIndex, moveActive, select, close],
  );

  return {
    isOpen,
    activeIndex,
    containerRef,
    open,
    close,
    toggle,
    setActiveIndex,
    select,
    onKeyDown,
    menuId,
    getOptionId,
    activeOptionId,
  };
}
